import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout/Layout';
import { useImageVariables } from '@/hooks/useImageVariables';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Search, Eye, Image } from 'lucide-react';
import type { ImageVariable } from '@/hooks/useImageVariables';

const PhotoGallery = () => {
  const { data: imageVariables = [] } = useImageVariables();
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedImage, setSelectedImage] = useState<ImageVariable | null>(null);

  // Only show variables that have images in the gallery
  const variablesWithImages = imageVariables.filter(v => v.image_url);

  // Filter variables based on search - only from those that have images
  const filteredVariables = variablesWithImages.filter(variable => {
    const matchesSearch = variable.variable_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
                         variable.title.toLowerCase().includes(searchFilter.toLowerCase()) ||
                         variable.keywords.some(k => k.toLowerCase().includes(searchFilter.toLowerCase()));
    
    return matchesSearch;
  });

  return (
    <Layout>
      {/* Hero Section with Background Image */}
      <section 
        className="relative bg-gradient-to-r from-brand-charcoal-gray to-brand-charcoal-gray/80 text-white py-20 md:py-28 overflow-hidden"
        style={{
          backgroundImage: `url('/lovable-uploads/05712f55-6608-4d58-b71e-fc4dd31ec82a.png')`,
          backgroundSize: '200px 200px',
          backgroundRepeat: 'repeat',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-brand-charcoal-gray/90"></div>
        
        <div className="relative container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-trophy-gold drop-shadow-lg">
              📸 Photo Gallery
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto drop-shadow-md">
              Our Mini Gallery of Random Jobs and Projects.
            </p>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-8">
              {/* Video Column */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="aspect-video">
                  <iframe 
                    width="100%" 
                    height="100%" 
                    src="https://www.youtube.com/embed/Hq-AEwK1yQc?si=cGfatYTWotBYPXjx" 
                    title="YouTube video player" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                    referrerPolicy="strict-origin-when-cross-origin" 
                    allowFullScreen
                    className="rounded-lg"
                  />
                </div>
              </div>

              {/* Right Side Cards */}
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500 flex-1">
                  <div className="flex items-center mb-4">
                    <div className="bg-green-50 p-3 rounded-full mr-4">
                      <span className="text-2xl">💰</span>
                    </div>
                    <h4 className="text-xl font-bold text-brand-charcoal-gray">
                      Customer Savings
                    </h4>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    The customer got their roof replaced and complimentary upgraded for the small price of their 
                    deductible which was <strong className="text-brand-charcoal-gray">$3,500</strong> at the time, 
                    effectively saving the hotel owners over <strong className="text-green-600">$100,000</strong> in 
                    out of pocket repairs.
                  </p>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-brand-carolina-blue flex-1">
                  <div className="flex items-center mb-4">
                    <div className="bg-brand-carolina-blue/10 p-3 rounded-full mr-4">
                      <span className="text-2xl">🏆</span>
                    </div>
                    <h4 className="text-xl font-bold text-brand-charcoal-gray">
                      The Trophy Difference
                    </h4>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Our thorough inspection process and insurance expertise turned a denial into a full approval, 
                    demonstrating why property owners trust Carolina Trophy Roofs for their most challenging claims.
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-brand-trophy-gold">
              <div className="flex items-center mb-4">
                <div className="bg-brand-trophy-gold/10 p-3 rounded-full mr-4">
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-xl font-bold text-brand-charcoal-gray">
                  Quality Inn Success Story
                </h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                This was a Quality Inn roof that was initially denied by their insurance company to repair. 
                We performed our re-inspection and submitted further evidence of storm damage, got the full roof 
                replacement approved by insurance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Controls */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search photos..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {variablesWithImages.length} photos in gallery
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredVariables.map((variable) => (
              <Card key={variable.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative aspect-square bg-gray-100">
                  <img
                    src={variable.image_url}
                    alt={variable.alt_tag}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <CardContent className="p-4">
                  <h3 className="font-semibold truncate mb-1">{variable.title}</h3>
                  <p className="text-sm text-gray-600 truncate mb-2">{variable.variable_name}</p>
                  
                  <div className="flex flex-wrap gap-1 mb-3">
                    {variable.keywords.slice(0, 2).map((keyword, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {keyword}
                      </Badge>
                    ))}
                    {variable.keywords.length > 2 && (
                      <Badge variant="secondary" className="text-xs">
                        +{variable.keywords.length - 2}
                      </Badge>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => setSelectedImage(variable)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>{variable.title}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="text-center">
                            <img
                              src={variable.image_url}
                              alt={variable.alt_tag}
                              className="max-h-64 mx-auto rounded-lg object-contain"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <strong>Variable:</strong> {variable.variable_name}
                            </div>
                            <div>
                              <strong>Use Case:</strong> {variable.use_case}
                            </div>
                            <div className="col-span-2">
                              <strong>Description:</strong> {variable.description}
                            </div>
                            <div className="col-span-2">
                              <strong>Keywords:</strong> {variable.keywords.join(', ')}
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredVariables.length === 0 && (
            <div className="text-center py-12">
              <Image className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No photos found</h3>
              <p className="text-gray-500">
                {searchFilter ? 'Try adjusting your search terms.' : 'Check back soon for new photos.'}
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default PhotoGallery;
